import React, { useMemo, useState } from 'react';
import { trainingReportApi } from '../../services/trainingReportApi';
import './TrainingReportGenerate.css';

const formatGeneratedAt = (value) => {
  if (!value) return 'Pendiente';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-MX');
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const TrainingReportGenerateStep = ({
  idReporte,
  accessToken,
  report,
  setReport,
  onPrevious,
  onBackToList,
}) => {
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [status, setStatus] = useState(null);

  const generated = report?.estado === 'generado';
  const pdfFileName = report?.nombre_archivo || `reporte_capacitacion_${idReporte}.pdf`;
  const wordFileName = `reporte_capacitacion_${idReporte}.docx`;
  const generatedAt = useMemo(
    () => formatGeneratedAt(report?.generado_en),
    [report?.generado_en],
  );

  const generateFinal = async () => {
    if (!accessToken || generating || downloading) return;

    const confirmed = window.confirm(
      generated
        ? 'Se regenerará el reporte definitivo. El PDF registrado será reemplazado y podrás volver a descargar PDF o Word editable. ¿Deseas continuar?'
        : 'Se generará el reporte definitivo y cambiará a estado GENERADO. Después podrás descargarlo en PDF o Word editable. ¿Deseas continuar?',
    );
    if (!confirmed) return;

    setGenerating(true);
    setStatus({
      type: 'info',
      message: generated
        ? 'Regenerando reporte definitivo...'
        : 'Generando reporte definitivo...',
    });

    try {
      const updated = await trainingReportApi.generate(idReporte, accessToken);
      setReport(updated);
      setStatus({
        type: 'success',
        message: generated
          ? 'Reporte definitivo regenerado correctamente. Ya puedes descargar PDF o Word editable.'
          : 'Reporte definitivo generado correctamente. Ya puedes descargar PDF o Word editable.',
      });
    } catch (error) {
      console.error('No se pudo generar el reporte definitivo:', error);
      setStatus({
        type: 'error',
        message:
          error?.message || 'No fue posible generar el reporte definitivo.',
      });
    } finally {
      setGenerating(false);
    }
  };

  const downloadFinal = async (format) => {
    if (!accessToken || !generated || generating || downloading) return;

    setDownloading(format);
    setStatus({
      type: 'info',
      message: format === 'word' ? 'Preparando Word editable...' : 'Preparando PDF...',
    });

    try {
      const blob =
        format === 'word'
          ? await trainingReportApi.downloadWord(idReporte, accessToken)
          : await trainingReportApi.download(idReporte, accessToken);
      downloadBlob(blob, format === 'word' ? wordFileName : pdfFileName);
      setStatus({
        type: 'success',
        message:
          format === 'word'
            ? 'Descarga de Word iniciada correctamente.'
            : 'Descarga de PDF iniciada correctamente.',
      });
    } catch (error) {
      console.error(`No se pudo descargar el reporte ${format}:`, error);
      setStatus({
        type: 'error',
        message:
          error?.message ||
          (format === 'word'
            ? 'No fue posible descargar el Word editable.'
            : 'No fue posible descargar el PDF definitivo.'),
      });
    } finally {
      setDownloading(null);
    }
  };

  const statusClass =
    status?.type === 'success'
      ? 'admin-operation-status-success'
      : status?.type === 'error'
        ? 'admin-operation-status-error'
        : 'admin-operation-status-warning';

  return (
    <>
      <div className="admin-operation-panel-heading">
        <div>
          <p className="admin-operation-eyebrow">Paso 7 de 7</p>
          <h2>Generar reporte definitivo</h2>
          <p>
            Genera la versión definitiva del reporte. Después podrás descargarla en
            PDF o Word editable.
          </p>
        </div>
        <span className={`training-report-status training-report-status-${report?.estado || 'borrador'}`}>
          {generated ? 'Generado' : 'Borrador'}
        </span>
      </div>

      <div
        className={`training-report-generate-hero ${
          generated
            ? 'training-report-generate-hero-generated'
            : 'training-report-generate-hero-ready'
        }`}
      >
        <div>
          <span>{generated ? 'Documento definitivo' : 'Listo para generar'}</span>
          <strong>
            {generated
              ? 'El reporte ya cuenta con una versión definitiva'
              : 'La vista previa fue revisada'}
          </strong>
          <small>
            {generated
              ? 'Puedes descargar el PDF registrado o una copia Word editable con los mismos datos.'
              : 'La generación vuelve a validar todos los requisitos en backend antes de habilitar las descargas en PDF y Word.'}
          </small>
        </div>

        <button
          className="admin-operation-button"
          type="button"
          onClick={generateFinal}
          disabled={generating || Boolean(downloading)}
        >
          {generating
            ? 'Generando...'
            : generated
              ? 'Regenerar reporte definitivo'
              : 'Generar reporte definitivo'}
        </button>
      </div>

      {status && (
        <div className={`admin-operation-status ${statusClass}`} role="status">
          {status.message}
        </div>
      )}

      {generated && (
        <>
          <div className="training-report-generate-details">
            <div className="training-report-generate-detail">
              <span>Archivo PDF</span>
              <strong>{pdfFileName}</strong>
            </div>
            <div className="training-report-generate-detail">
              <span>Word editable</span>
              <strong>{wordFileName}</strong>
            </div>
            <div className="training-report-generate-detail">
              <span>Generado</span>
              <strong>{generatedAt}</strong>
            </div>
            <div className="training-report-generate-detail training-report-field-wide">
              <span>SHA-256 del PDF</span>
              <code>{report?.hash_pdf || 'No disponible'}</code>
            </div>
          </div>

          <div className="admin-operation-status admin-operation-status-success">
            El PDF corresponde a la versión definitiva almacenada por el backend. El Word
            se genera al momento con los mismos datos y queda editable después de descargarlo.
          </div>
        </>
      )}

      <div className="admin-operation-status admin-operation-status-warning training-report-generate-warning">
        Si después modificas datos generales, participantes, asistencias, resultados o
        evidencias, el reporte volverá a BORRADOR y tendrás que revisar y generar una
        nueva versión definitiva.
      </div>

      <div className="training-report-editor-actions">
        <button
          className="admin-operation-button admin-operation-button-secondary"
          type="button"
          onClick={onPrevious}
          disabled={generating || Boolean(downloading)}
        >
          Volver a vista previa
        </button>
        {generated && (
          <>
            <button
              className="admin-operation-button training-report-generate-download"
              type="button"
              onClick={() => downloadFinal('pdf')}
              disabled={generating || Boolean(downloading)}
            >
              {downloading === 'pdf' ? 'Descargando PDF...' : 'Descargar PDF definitivo'}
            </button>
            <button
              className="admin-operation-button training-report-generate-download"
              type="button"
              onClick={() => downloadFinal('word')}
              disabled={generating || Boolean(downloading)}
            >
              {downloading === 'word' ? 'Generando Word...' : 'Descargar Word editable'}
            </button>
          </>
        )}
        <button
          className="admin-operation-button admin-operation-button-secondary"
          type="button"
          onClick={onBackToList}
          disabled={generating || Boolean(downloading)}
        >
          Volver al listado
        </button>
      </div>
    </>
  );
};
